<div class="settings-overlay" (click)="closeModal()">
  <div class="settings-card" role="dialog" aria-modal="true" aria-labelledby="settings-title" @modalAnimation (click)="$event.stopPropagation()">

    <div class="settings-header">
      <h2 id="settings-title" class="settings-title">Account Settings</h2>
      <button type="button" class="close-button" (click)="closeModal()" aria-label="Close settings">
        <i class="bi bi-x-lg" aria-hidden="true"></i>
      </button>
    </div>

    <div class="settings-body">

      <!-- ── Profile ───────────────────────────────────────────────────── -->
      <section class="settings-section">
        <h3 class="section-title"><i class="bi bi-person" aria-hidden="true"></i> Profile</h3>

        <div class="form-group">
          <input
            type="text"
            id="settings-name"
            class="form-input"
            placeholder="Display name"
            maxlength="60"
            autocomplete="name"
            [formControl]="nameControl"
            (keydown.enter)="saveName()"
          />
          <label for="settings-name" class="form-label">Display name</label>
        </div>
        <button type="button" class="btn-primary-sm" [disabled]="savingName || nameControl.pristine || !nameControl.value.trim()" (click)="saveName()">
          {{ savingName ? 'Saving…' : 'Save name' }}
        </button>

        <div class="email-display">
          <strong>Email:</strong> {{ email }}
          @if (emailVerified) {
            <span class="badge badge-ok"><i class="bi bi-patch-check-fill" aria-hidden="true"></i> Verified</span>
          } @else {
            <span class="badge badge-warn">Not verified</span>
          }
        </div>

        @if (!emailVerified) {
          <p class="info-text">Verify your email to unlock two-step verification.</p>
          <div class="btn-row">
            <button type="button" class="btn-secondary-sm" (click)="resendVerification()">Resend email</button>
            <button type="button" class="btn-secondary-sm" (click)="checkVerified()">I've verified</button>
          </div>
        }

        <p class="meta-line">
          Signed in with
          @if (usesGoogle) { <strong>Google</strong> }
          @if (usesGoogle && usesPassword) { and }
          @if (usesPassword) { <strong>email &amp; password</strong> }
        </p>
      </section>

      <!-- ── Security ──────────────────────────────────────────────────── -->
      <section class="settings-section">
        <h3 class="section-title"><i class="bi bi-shield-lock" aria-hidden="true"></i> Two-step verification</h3>
        <p class="info-text">
          Adds a second check when you sign in, so a stolen password alone isn't enough. Choose an
          authenticator app, a text message, or both.
        </p>

        @if (hasFactors) {
          <ul class="factor-list">
            @for (f of factors; track f.uid) {
              <li class="factor-item">
                <i class="bi" [class.bi-chat-dots]="f.kind === 'sms'" [class.bi-shield-lock]="f.kind === 'totp'" aria-hidden="true"></i>
                <div class="factor-text">
                  <strong>{{ f.label }}</strong>
                  @if (f.detail) { <span>{{ f.detail }}</span> }
                </div>
                @if (removeCandidate !== f.uid) {
                  <button type="button" class="link-danger" (click)="removeCandidate = f.uid">Remove</button>
                } @else {
                  <button type="button" class="btn-danger-sm" (click)="confirmRemove(f)" appThrottleClick>Confirm</button>
                  <button type="button" class="link-btn" (click)="removeCandidate = null">Keep</button>
                }
              </li>
            }
          </ul>
        }

        @switch (flow) {
          @case ('idle') {
            <div class="btn-row">
              @if (!hasTotp) {
                <button type="button" class="btn-primary-sm" [disabled]="!canEnroll || busy" (click)="startTotp()">
                  <i class="bi bi-phone" aria-hidden="true"></i> Authenticator app
                </button>
              }
              @if (!hasSms) {
                <button type="button" class="btn-secondary-sm" [disabled]="!canEnroll || busy" (click)="startSms()">
                  <i class="bi bi-chat-dots" aria-hidden="true"></i> Text message
                </button>
              }
            </div>
            @if (!canEnroll) {
              <p class="note">Verify your email first (see Profile above).</p>
            }
          }

          @case ('totp') {
            <app-two-factor-panel
              mode="enroll"
              [uri]="enrollment?.uri ?? null"
              [secretKey]="enrollment?.secretKey ?? null"
              [busy]="busy"
              [error]="error"
              (submitted)="onTotpCode($event)"
              (cancelled)="cancelFlow()">
            </app-two-factor-panel>
          }

          @case ('sms-phone') {
            <div class="flow-box">
              <div class="form-group">
                <input
                  type="tel"
                  id="settings-phone"
                  class="form-input"
                  placeholder="+2348012345678"
                  autocomplete="tel"
                  [formControl]="phoneControl"
                  (keydown.enter)="sendSmsCode()"
                />
                <label for="settings-phone" class="form-label">Mobile number</label>
                @if (phoneControl.invalid && phoneControl.touched) {
                  <div class="field-error">Use international format with the country code, e.g. +2348012345678.</div>
                }
              </div>
              <p class="note">Standard message rates may apply. We only use your number for sign-in codes.</p>
              @if (error) { <div class="error-message" role="alert">{{ error }}</div> }
              <div class="btn-row">
                <button type="button" class="btn-primary-sm" [disabled]="busy || phoneControl.invalid" (click)="sendSmsCode()">
                  {{ busy ? 'Sending…' : 'Text me a code' }}
                </button>
                <button type="button" class="btn-secondary-sm" [disabled]="busy" (click)="cancelFlow()">Cancel</button>
              </div>
            </div>
          }

          @case ('sms-code') {
            <app-two-factor-panel
              mode="sms"
              [busy]="busy"
              [error]="error"
              (submitted)="onSmsCode($event)"
              (cancelled)="cancelFlow()">
            </app-two-factor-panel>
          }

          @case ('reauth') {
            <div class="flow-box">
              <p class="info-text">For your security, confirm it's you before changing two-step verification.</p>
              @if (usesPassword) {
                <div class="form-group">
                  <input
                    type="password"
                    id="settings-reauth"
                    class="form-input"
                    placeholder="Current password"
                    autocomplete="current-password"
                    [formControl]="reauthPassword"
                    (keydown.enter)="submitReauth()"
                  />
                  <label for="settings-reauth" class="form-label">Current password</label>
                </div>
                @if (error) { <div class="error-message" role="alert">{{ error }}</div> }
                <div class="btn-row">
                  <button type="button" class="btn-primary-sm" [disabled]="busy || reauthPassword.invalid" (click)="submitReauth()">Confirm</button>
                  @if (usesGoogle) {
                    <button type="button" class="btn-secondary-sm" [disabled]="busy" (click)="submitReauth(true)">Use Google instead</button>
                  }
                  <button type="button" class="btn-secondary-sm" [disabled]="busy" (click)="cancelFlow()">Cancel</button>
                </div>
              } @else {
                @if (error) { <div class="error-message" role="alert">{{ error }}</div> }
                <div class="btn-row">
                  <button type="button" class="btn-primary-sm" [disabled]="busy" (click)="submitReauth(true)">Continue with Google</button>
                  <button type="button" class="btn-secondary-sm" [disabled]="busy" (click)="cancelFlow()">Cancel</button>
                </div>
              }
            </div>
          }

          @case ('mfa-reauth') {
            @if (mfaResolver) {
              <app-mfa-challenge
                [resolver]="mfaResolver"
                (verified)="onReauthMfaVerified()"
                (cancelled)="cancelFlow()">
              </app-mfa-challenge>
            }
          }
        }

        @if (flow === 'idle' && error) {
          <div class="error-message" role="alert">{{ error }}</div>
        }
      </section>

      <!-- ── Password ──────────────────────────────────────────────────── -->
      @if (usesPassword) {
        <section class="settings-section">
          <h3 class="section-title"><i class="bi bi-key" aria-hidden="true"></i> Password</h3>
          <p class="info-text">We'll email you a link to choose a new password.</p>
          <button type="button" class="btn-secondary-sm" (click)="sendPasswordReset()">Email me a reset link</button>
        </section>
      }

      <!-- ── Preferences ───────────────────────────────────────────────── -->
      <section class="settings-section">
        <h3 class="section-title"><i class="bi bi-sliders" aria-hidden="true"></i> Preferences</h3>
        <button type="button" class="toggle-row" role="switch" [attr.aria-checked]="isDarkMode$ | async" (click)="toggleTheme()">
          <span>Dark mode</span>
          <span class="switch" [class.on]="isDarkMode$ | async"><span class="knob"></span></span>
        </button>
        <a routerLink="/unsubscribe" class="toggle-row" (click)="closeModal()">
          <span>Manage newsletter emails</span>
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </a>
      </section>

      <!-- ── Session ───────────────────────────────────────────────────── -->
      <section class="settings-section">
        <button type="button" class="btn-secondary-sm full" (click)="signOut()">
          <i class="bi bi-box-arrow-right" aria-hidden="true"></i> Log out
        </button>
      </section>
    </div>

    <!-- invisible reCAPTCHA anchor (Firebase requires it before sending an SMS) -->
    <div #recaptcha></div>
  </div>
</div>
